import {
  AreaFromApi,
  BookingFromApi,
  ProcessedScanResult,
  ScanResultType
} from "../SpaceScanner.types";
import {
  isUnassignedResult,
  LOCKED_RESULT_TYPES,
  RESULT_TYPE_TEXT
} from "../SpaceScannerConstants";

/**
 * Extracts area IDs from an array of areas
 */
export const getAreaIds = (areas: AreaFromApi[] | undefined): string[] => {
  if (!areas || areas.length === 0) {
    return [];
  }
  return areas.map((area) => area.id);
};

/**
 * Extracts zone IDs from an array of areas
 */
export const getZoneIds = (areas: AreaFromApi[] | undefined): string[] => {
  if (!areas || areas.length === 0) {
    return [];
  }
  return areas
    .map((area) => area.zone)
    .filter((zone): zone is string => zone !== undefined);
};

/**
 * Checks if two area arrays have overlapping area IDs
 */
export const hasOverlappingAreas = (
  areas1: AreaFromApi[] | undefined,
  areas2: AreaFromApi[] | undefined
): boolean => {
  const ids1 = getAreaIds(areas1);
  const ids2 = getAreaIds(areas2);
  return ids1.some((id) => ids2.includes(id));
};

/**
 * Checks if two area arrays have overlapping zones
 */
export const hasOverlappingZones = (
  areas1: AreaFromApi[] | undefined,
  areas2: AreaFromApi[] | undefined
): boolean => {
  const zones1 = getZoneIds(areas1);
  const zones2 = getZoneIds(areas2);
  return zones1.some((zone) => zones2.includes(zone));
};

/**
 * Finds all booking IDs that depend on the given booking's movement.
 * Dependencies are determined by overlapping areas or zones.
 * Uses recursive traversal to find the full dependency chain.
 */
export const getDependentBookingIds = (
  bookingId: string,
  results: ProcessedScanResult[]
): string[] => {
  const visited = new Set<string>();

  const findDependents = (currentBookingId: string): string[] => {
    if (visited.has(currentBookingId)) {
      return [];
    }
    visited.add(currentBookingId);

    const booking = results.find((r) => r.booking._id === currentBookingId);
    if (!booking) {
      return [];
    }

    const dependentIds: string[] = [];

    for (const other of results) {
      if (
        other.booking._id === currentBookingId ||
        visited.has(other.booking._id) ||
        other.shuffleLocked
      ) {
        continue;
      }

      const hasDependency =
        hasOverlappingAreas(other.newAreas, booking.previousAreas) ||
        hasOverlappingAreas(other.previousAreas, booking.newAreas) ||
        hasOverlappingAreas(other.newAreas, booking.newAreas) ||
        hasOverlappingZones(other.newAreas, booking.previousAreas) ||
        hasOverlappingZones(other.previousAreas, booking.newAreas);

      if (hasDependency && other.moved) {
        dependentIds.push(other.booking._id);
        const nestedDependents = findDependents(other.booking._id);
        dependentIds.push(...nestedDependents);
      }
    }

    return dependentIds;
  };

  return findDependents(bookingId);
};

/**
 * Converts an array of areas to a comma-separated string of area names
 */
export const getAreaNames = (areas: AreaFromApi[] | undefined): string => {
  if (!areas || areas.length === 0) {
    return "";
  }
  return areas.map((area) => area.name).join(", ");
};

/**
 * Returns the customer name from a BookingFromApi object.
 * Mirrors the logic of getCustomerName in utils/booking.ts but works with snake_case fields.
 */
export const getBookingCustomerName = (booking: BookingFromApi): string => {
  if (booking.walk_in) {
    return "Walk In";
  }

  const firstName = booking.first_name?.trim() || "";
  const lastName = booking.last_name?.trim() || "";

  if (firstName || lastName) {
    return `${firstName} ${lastName}`.trim();
  }

  return "No Name";
};

/**
 * Returns a human-readable reason text for a scan result type
 */
export const getReasonText = (resultType: string): string => {
  return RESULT_TYPE_TEXT[resultType as ScanResultType]?.shortReason ?? "";
};

type EmptyResultsReasonCounts = {
  lockedCount: number;
  startedCount: number;
  guestlistCount: number;
  optimalCount: number;
  leftCount: number;
  waitlistedCount: number;
  noshowCount: number;
  areaClosedCount: number;
  unassignedCount: number;
};

/**
 * Calculates the counts for different result types in the scan results
 */
export const getResultCounts = (
  scanResult: ProcessedScanResult[]
): EmptyResultsReasonCounts => {
  const lockedCount = scanResult.filter(
    (r) => LOCKED_RESULT_TYPES.includes(r.result) || r.shuffleLocked
  ).length;
  const startedCount = scanResult.filter(
    (r) => r.result === "booking_started"
  ).length;
  const guestlistCount = scanResult.filter(
    (r) => r.result === "booking_guestlist"
  ).length;
  const optimalCount = scanResult.filter(
    (r) => r.result === "no_better_area" || r.result === "preferred_area_found"
  ).length;
  const leftCount = scanResult.filter(
    (r) => r.result === "booking_left"
  ).length;
  const waitlistedCount = scanResult.filter(
    (r) => r.result === "booking_waitlisted"
  ).length;
  const noshowCount = scanResult.filter(
    (r) => r.result === "booking_noshow"
  ).length;
  const areaClosedCount = scanResult.filter(
    (r) => r.result === "area_closed"
  ).length;
  const unassignedCount = scanResult.filter((r) =>
    isUnassignedResult(r.result)
  ).length;

  return {
    lockedCount,
    startedCount,
    guestlistCount,
    optimalCount,
    leftCount,
    waitlistedCount,
    noshowCount,
    areaClosedCount,
    unassignedCount
  };
};

type ReasonConfig = {
  count: number;
  buildMessage: (count: number) => string;
};

/**
 * Builds reason messages from a configuration array
 * This approach reduces cognitive complexity by using a data-driven pattern
 */
const buildReasonMessages = (configs: ReasonConfig[]): string[] => {
  const reasons: string[] = [];
  for (const { count, buildMessage } of configs) {
    if (count > 0) {
      reasons.push(buildMessage(count));
    }
  }
  return reasons;
};

/**
 * Generates an array of human-readable reasons explaining why no bookings can be moved
 */
export const getEmptyResultsReason = (
  scanResult: ProcessedScanResult[]
): string[] => {
  const {
    lockedCount,
    startedCount,
    guestlistCount,
    optimalCount,
    leftCount,
    waitlistedCount,
    noshowCount,
    areaClosedCount,
    unassignedCount
  } = getResultCounts(scanResult);

  const configs: ReasonConfig[] = [
    {
      count: optimalCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s are" : " is"} already optimally assigned`
    },
    {
      count: lockedCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s are" : " is"} locked`
    },
    {
      count: startedCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s have" : " has"} already started`
    },
    {
      count: guestlistCount,
      buildMessage: (count) =>
        `${count} ${count !== 1 ? "are" : "is a"} guestlist booking${count !== 1 ? "s" : ""}`
    },
    {
      count: leftCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s have" : " has"} left`
    },
    {
      count: waitlistedCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s are" : " is"} on the waitlist`
    },
    {
      count: noshowCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s are" : " is"} marked as no-show`
    },
    {
      count: areaClosedCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s have" : " has"} closed target areas`
    },
    {
      count: unassignedCount,
      buildMessage: (count) =>
        `${count} booking${count !== 1 ? "s" : ""} could not be assigned`
    }
  ];

  return buildReasonMessages(configs);
};
